import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { useHistory } from 'react-router';
import moment from 'moment';

import { customerInfo as customerInfoShape } from 'constants/propTypes';
import Box from 'components/ui/Box';

import countries from '../../../constants/countries';
import {
  SectionTitle,
  ActionsList,
  SectionContainer,
  AccountInfoSection,
  AccountInfoContainer,
  AccountInfoColumn
} from '../ClubEurostarMember.styles';
import { StyledButton } from '../../HomePage/HomePage.styles';

import DetailsItem from './DetailsItem';

const AccountDetails = ({ customerInfo, canPerformOperations, isLoungeStaffUser }) => {
  const { push } = useHistory();

  const customerNationality = customerInfo.profile?.nationality;
  const nationality = customerNationality && countries.find((c) => c.code === customerNationality)?.name;

  const customerDateOfBirth = moment(customerInfo.profile.dateOfBirth);

  const membershipNumber = customerInfo.profile?.loyalty?.membershipNumber;

  return (
    <SectionContainer>
      <ActionsList>
        <StyledButton
          onClick={() => push(`/search?membershipNumber=${membershipNumber}`)}
          disabled={!canPerformOperations}
          showTip={!canPerformOperations}
          textIdTip={isLoungeStaffUser ? 'loungeStaff.restrictedAccess' : 'clubEurostar.deactivatedAccount'}
        >
          <FormattedMessage id="clubEurostar.bookWithoutSub" />
        </StyledButton>
      </ActionsList>
      <AccountInfoSection data-cy="customer-account-details">
        <AccountInfoContainer align="flex-start">
          <AccountInfoColumn>
            <SectionTitle>
              <FormattedMessage id="clubEurostar.personalDetails" />
            </SectionTitle>
            <Box margin="25px 0">
              <DetailsItem
                dataTestid="clubEurostar-name"
                labelTranslationKey="clubEurostar.name"
                value={`${customerInfo.firstName} ${customerInfo.lastName}`}
                type="input"
              />

              <DetailsItem
                dataTestid="paxForm-loyaltyNumber"
                labelTranslationKey="paxForm.loyaltyNumber"
                value={customerInfo.profile.loyalty.membershipNumber}
                type="input"
              />
              <DetailsItem
                dataTestid="clubEurostar-nationality"
                labelTranslationKey="clubEurostar.nationality"
                value={nationality}
                type="input"
              />
            </Box>
            {customerDateOfBirth.isValid() ? (
              <Box margin="25px 0">
                <DetailsItem
                  dataTestid="paxForm-birthDate"
                  type="date-input"
                  labelTranslationKey="paxForm.birthDate"
                  value={customerDateOfBirth.format('DD MM YYYY')}
                />
              </Box>
            ) : null}
            <Box margin="25px 0">
              <DetailsItem
                dataTestid="billingDetailsForm-street"
                type="input"
                labelTranslationKey="billingDetailsForm.street"
                value={customerInfo.address.street}
              />
              <DetailsItem
                dataTestid="billingDetailsForm-city"
                type="input"
                labelTranslationKey="billingDetailsForm.city"
                value={customerInfo.address.city}
              />
              <DetailsItem
                dataTestid="billingDetailsForm-postcode"
                type="input"
                labelTranslationKey="billingDetailsForm.postcode"
                value={customerInfo.address.postalCode}
              />
              <DetailsItem
                dataTestid="billingDetailsForm-country"
                type="input"
                labelTranslationKey="billingDetailsForm.country"
                value={customerInfo.address.countryCode}
              />
            </Box>
            {customerInfo.favouriteTravellers?.length > 0 && (
              <>
                <SectionTitle>
                  <FormattedMessage id="clubEurostar.savedPassengers" />
                </SectionTitle>
                {customerInfo.favouriteTravellers.map((traveller) => (
                  <Box key={traveller.membershipNumber} margin="25px 0">
                    <DetailsItem
                      dataTestid="clubEurostar-favouriteTravellers-name"
                      labelTranslationKey="clubEurostar.name"
                      value={`${traveller.firstName} ${traveller.lastName}`}
                      type="input"
                    />
                    <DetailsItem
                      dataTestid="clubEurostar-favouriteTravellers-membership"
                      labelTranslationKey="paxForm.loyaltyNumber"
                      value={traveller.membershipNumber}
                      type="input"
                    />
                  </Box>
                ))}
              </>
            )}
          </AccountInfoColumn>
          <AccountInfoColumn>
            <SectionTitle>
              <FormattedMessage id="clubEurostar.contactDetails" />
            </SectionTitle>
            <Box margin="25px 0">
              <DetailsItem
                dataTestid="clubEurostar-emailAddress"
                type="input"
                labelTranslationKey="clubEurostar.emailAddress"
                value={customerInfo.emailAddress}
              />
              <DetailsItem
                dataTestid="clubEurostar-contactNumber"
                type="input"
                labelTranslationKey="clubEurostar.contactNumber"
                value={customerInfo.phoneNumber}
              />
            </Box>
            <SectionTitle>
              <FormattedMessage id="clubEurostar.marketingPreferences" />
            </SectionTitle>
            <Box margin="25px 0">
              <DetailsItem
                dataTestid="homepage-clubEurostar"
                type="checkbox"
                labelTranslationKey="homepage.clubEurostar"
                value={customerInfo.marketingPreferences.clubEurostarOptin}
              />
              <DetailsItem
                dataTestid="clubEurostar-trains"
                type="checkbox"
                labelTranslationKey="clubEurostar.trains"
                value={customerInfo.marketingPreferences.trainsOptin}
              />
              <DetailsItem
                dataTestid="clubEurostar-hotels"
                type="checkbox"
                labelTranslationKey="clubEurostar.hotels"
                value={customerInfo.marketingPreferences.hotelsOptin}
              />
              <DetailsItem
                dataTestid="clubEurostar-trips"
                type="checkbox"
                labelTranslationKey="clubEurostar.trips"
                value={customerInfo.marketingPreferences.tripsOptin}
              />
            </Box>
            <SectionTitle>
              <FormattedMessage id="clubEurostar.secondContact" />
            </SectionTitle>
            <Box margin="25px 0">
              <DetailsItem
                dataTestid="clubEurostar-nominatedPerson"
                type="input"
                labelTranslationKey="clubEurostar.nominatedPerson"
                value={customerInfo.nominatedPerson}
              />
              <DetailsItem
                dataTestid="clubEurostar-nominatedPassphrase"
                type="input"
                labelTranslationKey="clubEurostar.nominatedPassphrase"
                value={customerInfo.nominatedPassphrase}
              />
              {customerInfo.nominatedPerson2 && (
                <DetailsItem
                  dataTestid="clubEurostar-nominatedPerson2"
                  type="input"
                  labelTranslationKey="clubEurostar.nominatedPerson2"
                  value={customerInfo.nominatedPerson2}
                />
              )}
              {customerInfo.nominatedPassphrase2 && (
                <DetailsItem
                  dataTestid="clubEurostar-nominatedPassphrase2"
                  type="input"
                  labelTranslationKey="clubEurostar.nominatedPassphrase2"
                  value={customerInfo.nominatedPassphrase2}
                />
              )}
            </Box>
          </AccountInfoColumn>
        </AccountInfoContainer>
      </AccountInfoSection>
    </SectionContainer>
  );
};

AccountDetails.propTypes = {
  customerInfo: customerInfoShape,
  canPerformOperations: PropTypes.bool,
  isLoungeStaffUser: PropTypes.bool
};

export default AccountDetails;
